import Agilite from 'agilite'
import { firebaseAuth } from '../../..'
import { formatDateOfBirth, handleError } from '../../lib/utils'
import store from '../../../store'
import { getDataWithSync, updateItemWithSync } from '../../../db/dbSync'
import { STORES } from '../../../db/indexedDB'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

export const findAgiliteUser = (filter, isActiveOnly = false, projection) => {
  return new Promise((resolve, reject) => {
    let qry = { $or: [] }
    let response = null

    ;(async () => {
      try {
        for (const prop in filter) {
          qry.$or.push({ [prop]: filter[prop] })
        }

        if (isActiveOnly) {
          qry['extraData.isActive'] = isActiveOnly
        }

        response = await agilite.Connectors.execute('auth_users', 'find', { filter: JSON.stringify(qry), projection })

        if (response.data.length > 0) {
          resolve(response.data[0])
        } else {
          resolve(null)
        }
      } catch (e) {
        reject(handleError(e, true))
      }
    })()
  })
}

export const createAgiliteUser = (data, useFirebaseId = true, createdBy) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      const State = store.getState()
      let response = null
      let timestamp = Date()
      let retryCount = 0
      const maxRetries = 5

      try {
        data.extraData = { ...data.extraData }

        if (State.auth?.agiliteUser?.extraData?.role?.type !== 'super_admin') {
          data.extraData.entities = [State.core.entity._id]
        }

        data.dateCreated = timestamp
        data.dateModified = timestamp

        // Formatting
        if (data.dateOfBirth) {
          data.dateOfBirth = formatDateOfBirth(data.dateOfBirth)
        }

        // Retry logic for billingId conflicts
        while (retryCount <= maxRetries) {
          try {
            // Get the user with the highest billingId using correct Agilite API format
            const lastUserResponse = await agilite.Connectors.execute('auth_users', 'find', {
              filter: JSON.stringify({}),
              projection: 'billingId',
              options: JSON.stringify({ sort: { billingId: 'desc' } }), // Correct Agilite format
              page: 1,
              pageLimit: 1
            })

            let nextBillingId = 1 // Default for first user
            if (lastUserResponse.data && lastUserResponse.data.length > 0) {
              const lastUser = lastUserResponse.data[0]
              console.log('🔍 Debug - Full query response:', lastUserResponse.data)
              console.log('🔍 Debug - First user returned:', lastUser)
              console.log('🔍 Debug - billingId from first user:', lastUser.billingId)
              nextBillingId = (lastUser.billingId || 0) + 1
            } else {
              console.log('🔍 Debug - No users found in response')
            }

            console.log(
              `🔄 Attempt ${retryCount + 1}: Highest billingId found = ${
                nextBillingId - 1
              }, assigning next billingId = ${nextBillingId}`
            )

            response = await agilite.Connectors.execute('auth_users', 'create', {
              data: JSON.stringify({
                ...data,
                billingId: nextBillingId,
                firebaseId: useFirebaseId ? firebaseAuth.currentUser.uid : '',
                createdBy: createdBy ? createdBy : 'System'
              })
            })

            console.log(`✅ User created successfully with billingId = ${nextBillingId}`)
            resolve(response.data)
            return
          } catch (createError) {
            // Check if it's a duplicate billingId error
            if (
              createError.response?.data?.errorMessage?.includes('billingId') &&
              createError.response?.data?.errorMessage?.includes('duplicate key')
            ) {
              retryCount++
              console.log(`❌ BillingId collision detected. Retry ${retryCount}/${maxRetries}`)

              if (retryCount > maxRetries) {
                console.log('🚫 Max retries exceeded for billingId generation')
                reject(handleError(createError, true))
                return
              }

              // Exponential backoff: wait 100ms, 200ms, 400ms, etc.
              const delay = 100 * Math.pow(2, retryCount - 1)
              await new Promise((resolve) => setTimeout(resolve, delay))
            } else {
              // If it's not a billingId duplicate error, throw immediately
              reject(handleError(createError, true))
              return
            }
          }
        }
      } catch (e) {
        reject(handleError(e, true))
      }
    })()
  })
}

export const updateAgiliteUser = (recordId, data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null
      let timestamp = Date()

      try {
        data.dateModified = timestamp

        // Formatting
        if (data.dateOfBirth) {
          data.dateOfBirth = formatDateOfBirth(data.dateOfBirth)
        }

        response = await updateItemWithSync(STORES.AUTH_USERS, {
          body: {
            recordId,
            data: JSON.stringify(data)
          },
          requestConfig: {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'profile-key': 'auth_users',
              'route-key': 'update'
            }
          }
        })

        resolve(response)
      } catch (e) {
        reject(handleError(e, true))
      }
    })()
  })
}

export const getCountries = (filter) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await getDataWithSync(STORES.COUNTRIES, {
          body: {
            filter: JSON.stringify(filter)
          },
          requestConfig: {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'profile-key': 'countries',
              'route-key': 'find'
            }
          }
        })
        resolve(response)
      } catch (e) {
        reject(handleError(e, true))
      }
    })()
  })
}
